import { RefreshButton } from '@/components/core';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { useAuthStore } from '@/state/store/auth';
import { useCurrentOrgId } from '@/store/use-my-oganization-store';
import { FileText, Globe } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { NewTemplateDialog } from '../components/new-templates-dialog';
import { OrgTemplatesList } from '../components/templates/org-templates-list';
import { OrgTemplatesToolbar } from '../components/templates/org-templates-toolbar';
import { PublicTemplatesList } from '../components/templates/public-templates-list';
import { PublicTemplatesToolbar } from '../components/templates/public-templates-toolbar';
import { ORG_TEMPLATES_QUERY_KEY, useOrgTemplates } from '../components/templates/use-org-templates';
import {
  PUBLIC_TEMPLATES_QUERY_KEY,
  usePublicTemplates,
} from '../components/templates/use-public-templates';
import { useGetOrganizationByOrgId } from '@/graphql/queries/organizations';

type TemplatesTab = 'org' | 'public';

export default function OrgTemplatesPage() {
  const { t } = useTranslation();
  const { orgId: slug } = useParams<{ orgId: string }>();
  const navigate = useNavigate();
  const orgId = useCurrentOrgId();
  const activeOrgRole = useAuthStore((state) => state.activeOrgRole);
  const { data: org, isLoading: orgLoading } = useGetOrganizationByOrgId(slug);

  const isAdmin = activeOrgRole === 'org_admin';
  const canManage = isAdmin || activeOrgRole === 'org_editor';
  const orgSlug = slug ?? '';

  const [tab, setTab] = useState<TemplatesTab>('org');
  const orgTemplates = useOrgTemplates(orgId);
  const publicTemplates = usePublicTemplates(orgId);

  if (orgLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <FileText className="h-8 w-8 animate-pulse text-selise-blue" />
      </div>
    );
  }

  if (!org) {
    navigate(`/`);
    return null;
  }

  const activeQueryKey = tab === 'org' ? ORG_TEMPLATES_QUERY_KEY : PUBLIC_TEMPLATES_QUERY_KEY;

  return (
    <div className="space-y-6 font-nunito">
      {/* Header */}
      <div>
        <div className="flex items-center justify-between">
          <h1 className="text-lg md:text-2xl font-bold text-[hsl(var(--fg))] font-heading">
            {t('ORGTEMPLATES_TITLE')}
          </h1>
          {/* Desktop only — on mobile the same dialog button lives in the
              OrgShell mobile topbar so it stays accessible while
              eliminating the duplicate inside this page. */}
          {canManage && (
            <div className="hidden md:flex gap-3">
              <RefreshButton queryKey={activeQueryKey} />
              <NewTemplateDialog orgSlug={orgSlug} orgName={org.Name} orgId={org.BlocksOrgId} />
            </div>
          )}
        </div>
        <p className="mt-1.5 md:mt-3 text-globe-grey">{t('ORGTEMPLATES_SUBTITLE')}</p>
      </div>

      <Tabs value={tab} onValueChange={(value) => setTab(value as TemplatesTab)}>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            <TabsTrigger value="org" className="gap-1.5 flex-1 md:flex-none">
              <FileText className="h-4 w-4" />
              {t('ORGTEMPLATES_TABS_ORG')}
            </TabsTrigger>
            <TabsTrigger value="public" className="gap-1.5 flex-1 md:flex-none">
              <Globe className="h-4 w-4" />
              {t('ORGTEMPLATES_TABS_PUBLIC')}
            </TabsTrigger>
          </TabsList>

          {/* Search + filter controls for the active tab, right-aligned in the tab row */}
          {tab === 'org' ? (
            <OrgTemplatesToolbar controller={orgTemplates} />
          ) : (
            <PublicTemplatesToolbar controller={publicTemplates} />
          )}
        </div>

        <TabsContent value="org" className="mt-4">
          <OrgTemplatesList
            controller={orgTemplates}
            org={org}
            orgSlug={orgSlug}
            canManage={canManage}
            isAdmin={isAdmin}
          />
        </TabsContent>

        <TabsContent value="public" className="mt-4">
          <PublicTemplatesList controller={publicTemplates} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
